import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Box,
  CircularProgress,
  FormControl,
  Grid,
  List,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { AptorApi, useAptorApi } from '../../../../../Api';
import { IGridQueryRequest, OrderDirection, PropertyError } from '../../../../../Api/AptorApi';
import { BackButton, Loading, Panel } from '../../../../../Components';
import { SelectField } from '../../../../../Components/Field';
import { TypedFormFieldValue, SelectOption } from '../../../../../Components/Form/Form.types';
import { UserContext } from '../../../../../Context/UserContext/UserContext';
import { useApiValidationSnackbar } from '../../../../../Api/useValidationSnackbar';
import { LawQuestionGroup, CustomerQuestions } from '../../../../Administration/Features/LawPortal/CustomerLaws/CompanyUnits/Checklist/Checklist.Group/Checklist.Group';
import { LawQuestion } from '../../../../Administration/Features/LawPortal/CustomerLaws/CompanyUnits/Checklist/Checklist.Question/Checklist.Question';
import { CustomerQuestionForm } from '../../../../Administration/Features/LawPortal/CustomerLaws/CompanyUnits/Checklist/Checklist.Form';
import { useList } from '../../../../../Hooks/useList';
import { useChecklist } from '../../../../../Hooks/useChecklist';
import { useStyles } from '../../../LawPortal.styles';
import { StepButtonBar } from '../EditLegalListWizard/StepButtonBar';

interface IChecklist {
  law: string;
  items: Array<IQuestion | IGroup>;
  companyUnitQuestions: ICustomerQuestion[];
}

interface ICustomerQuestion {
  id?: number;
  name: string;
}

interface IQuestion {
  id: number;
  name: string;
  type: 'question';
  hidden: boolean;
}

interface IGroup {
  id: number;
  name: string;
  type: 'group';
  groupItems: IQuestion[];
}

interface ICompanyUnitApiResult {
  id: number;
  name: string;
}
type GetCompanyUnitName = (formatMessage: any, isSingular: boolean) => string;

export const LawComplianceQuestions = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const id = parseInt(idParam || '0', 10);
  const { api, abortController } = useAptorApi();
  const { getCompanyUnitName } = useContext(UserContext);
  const [companyUnits, setCompanyUnits] = useState<SelectOption[]>([]);
  const [selectedUnits, setSelectedUnits] = useState<SelectOption[]>([]);
  const [loadingUnits, setLoadingUnits] = useState(true);

  useEffect(() => {
    const request: IGridQueryRequest = { page: 0, pageSize: 100, orderBy: 'name', orderDirection: OrderDirection.Ascending };
    api.searchLawCompanyUnits<ICompanyUnitApiResult>(id, request).then((result) => {
      if (abortController.current.signal.aborted) return;
      const options: SelectOption[] = (result?.data || []).map((unit) => ({ label: unit.name, value: unit.id }));
      setCompanyUnits(options);
      if (options.length === 1) {
        setSelectedUnits(options);
      }
      setLoadingUnits(false);
    });
  }, [id]);

  const handleUnitSelect = useCallback((units: SelectOption | SelectOption[]) => {
    setSelectedUnits(Array.isArray(units) ? units : [units]);
  }, []);

  const shouldBackToUnitSelection = companyUnits.length > 1 && selectedUnits.length > 0;

  if (loadingUnits) {
    return <Loading />;
  }

  return (
    <>
      <Grid item xs={12}>
        {shouldBackToUnitSelection ? (
          <BackButton disableMargin link="" onclick={() => setSelectedUnits([])} />
        ) : (
          <BackButton disableMargin link="/law-portal/our-laws/" />
        )}
      </Grid>
      <Grid item md={9} xs={12}>
        {selectedUnits.length === 0 ? (
          <CompanyUnitSelection
            onNext={handleUnitSelect}
            options={companyUnits}
            getCompanyUnitName={getCompanyUnitName}
          />
        ) : (
          <ChecklistPage
            lawId={id}
            selectedUnits={selectedUnits}
            allCompanyUnits={companyUnits}
            getCompanyUnitName={getCompanyUnitName}
          />
        )}
      </Grid>
    </>
  );
};

const CompanyUnitSelection = ({
  options,
  onNext,
  getCompanyUnitName,
}: {
  options: SelectOption[];
  onNext: (units: SelectOption | SelectOption[]) => void;
  getCompanyUnitName: GetCompanyUnitName;
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();
  const [selected, setSelected] = useState<SelectOption | SelectOption[]>();

  const handleChange = useCallback(
    (value: TypedFormFieldValue<number> | TypedFormFieldValue<number>[] | undefined) => {
      setSelected(value as SelectOption | SelectOption[] | undefined);
    },
    [],
  );

  const hasSelection = !!selected && (!Array.isArray(selected) || selected.length > 0);

  return (
    <Panel autoHeight>
      <Box className={classes.unitSelectionRow}>
        <FormControl className={classes.unitSelectControl}>
          <SelectField
            name="companyUnit"
             placeholder={formatMessage(
                  { id: 'law-portal.manage.update-laws.checklist.companyUnit.placeholder' },
                  { alias: getCompanyUnitName(formatMessage, false) },
                )}
            options={options}
            errorTexts={[]}
            initialState={selected}
            onChange={handleChange}
          />
        </FormControl>
        <Box className={classes.nextButtonOffset}>
          <StepButtonBar
            onCancel={() => {}}
            onNext={() => selected && onNext(selected)}
            nextLabel="form.next"
            nextDisabled={!hasSelection}
            formatMessage={formatMessage}
            showBack={false}
          />
        </Box>
      </Box>
    </Panel>
  );
};
const ChecklistPage = ({
  lawId,
  selectedUnits,
  allCompanyUnits,
  getCompanyUnitName,
}: {
  lawId: number;
  selectedUnits: SelectOption[];
  allCompanyUnits: SelectOption[];
  getCompanyUnitName: GetCompanyUnitName;
}) => {
  const sourceUnitId = selectedUnits[0].value as number;

  return (
    <Grid container>
      <Grid item xs={12}>
        <ChecklistEditor
          lawId={lawId}
          unitId={sourceUnitId}
          selectedUnits={selectedUnits}
          allCompanyUnits={allCompanyUnits}
          getCompanyUnitName={getCompanyUnitName}
        />
      </Grid>
    </Grid>
  );
};
const ChecklistEditor = ({
  lawId,
  unitId,
  selectedUnits,
  allCompanyUnits,
  getCompanyUnitName,
}: {
  lawId: number;
  unitId: number;
  selectedUnits: SelectOption[];
  allCompanyUnits: SelectOption[];
  getCompanyUnitName: GetCompanyUnitName;
}) => {
  const { api, abortController } = useAptorApi();
  const { notifySuccess, notifyUnhandledException, notifyValidationErrors } = useApiValidationSnackbar();
  const { formatMessage } = useIntl();
  const classes = useStyles();

  const [law, setLaw] = useState<string>();
  const [loaded, setLoaded] = useState(false);
  const [companyUnitQuestions, loadCompanyUnitQuestions, addCustomerQuestion, , deleteCustomerQuestion] =
    useList<ICustomerQuestion>();
  const [questions, loadQuestions, updateQuestion] = useChecklist<IQuestion, IGroup>();
  const [additionalUnits, setAdditionalUnits] = useState<SelectOption[]>([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const showAdditionalUnitsDropdown = allCompanyUnits.length > 1 && selectedUnits.length === 1;
  const additionalUnitOptions = useMemo(
    () =>
      showAdditionalUnitsDropdown
        ? allCompanyUnits.filter((unit) => unit.value !== selectedUnits[0].value)
        : [],
    [showAdditionalUnitsDropdown, allCompanyUnits, selectedUnits],
  );

  const additionalUnitNames = useMemo(
    () => additionalUnits.map((unit) => unit.label).join(', '),
    [additionalUnits],
  );

  const handleAdditionalUnitsChange = useCallback(
    (value: TypedFormFieldValue<number> | TypedFormFieldValue<number>[] | undefined) => {
      const next = value ? (Array.isArray(value) ? value : [value]) : [];
      setAdditionalUnits(next as SelectOption[]);
    },
    [],
  );

  useEffect(() => {
    setLoaded(false);
    api.getCustomerLawChecklist<IChecklist>(lawId, unitId).then((checklist) => {
      if (abortController.current.signal.aborted) return;
      setLaw(checklist.law);
      loadQuestions(checklist.items);
      loadCompanyUnitQuestions(checklist.companyUnitQuestions);
      setLoaded(true);
    });
  }, [lawId, unitId]);

  const onToggleHidden = useCallback(
    (questionId: number, groupId: number | undefined, hidden: boolean) => {
      updateQuestion(questionId, groupId, { hidden });
    },
    [updateQuestion],
  );

  const getHiddenQuestionIds = useCallback(() => {
    const hiddenStandaloneIds = questions
      .filter((item): item is IQuestion => item.type === 'question' && item.hidden)
      .map((item) => item.id);

    const hiddenGroupItemIds = questions
      .filter((item): item is IGroup => item.type === 'group')
      .flatMap((group) => group.groupItems)
      .filter((item) => item.hidden)
      .map((item) => item.id);

    return [...hiddenStandaloneIds, ...hiddenGroupItemIds];
  }, [questions]);

  const saveChecklist = useCallback(async () => {
    const hiddenQuestions = getHiddenQuestionIds();
    const companyUnitQuestionsForOtherUnits = companyUnitQuestions.map(({ id, ...rest }) => rest);

    const action = async (i: AptorApi) => {
      await i.updateCustomerLawChecklist(lawId, selectedUnits[0].value as number, {
        hiddenQuestions,
        companyUnitQuestions,
      });

      for (const unit of additionalUnits) {
        await i.updateCustomerLawChecklist(lawId, unit.value as number, {
          hiddenQuestions,
          companyUnitQuestions: companyUnitQuestionsForOtherUnits,
        });
      }
      notifySuccess();
    };
    const onValidation = (errors: PropertyError[]) => notifyValidationErrors(errors);
    await api.invoke(action, abortController.current, onValidation, notifyUnhandledException);
  }, [getHiddenQuestionIds, companyUnitQuestions, additionalUnits, selectedUnits, lawId]);

  const handleSaveClick = useCallback(() => {
    if (additionalUnits.length > 0) {
      setConfirmDialogOpen(true);
    } else {
      saveChecklist();
    }
  }, [additionalUnits, saveChecklist]);

  const handleConfirmSave = useCallback(async () => {
    setConfirmDialogOpen(false);
    await saveChecklist();
  }, [saveChecklist]);

  const closeConfirmDialog = useCallback(() => setConfirmDialogOpen(false), []);

  if (!loaded) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Panel autoHeight titleKey="admin.law-portal.law.checklist.heading" titleValues={{ name: law }}>
      <List className={`${classes.root} ${classes.questionList}`}>
        {questions.map((item: IQuestion | IGroup, index) =>
          item.type === 'question' ? (
            <LawQuestion key={item.id} {...item} index={index} toggleHidden={onToggleHidden} />
          ) : (
            <LawQuestionGroup key={item.id} {...item} toggleHidden={onToggleHidden} />
          ),
        )}
        {companyUnitQuestions.length > 0 && (
          <CustomerQuestions
            key="customer-questions"
            questions={companyUnitQuestions}
            loadCompanyUnitQuestionsQuestions={loadCompanyUnitQuestions}
            remove={deleteCustomerQuestion}
            companyUnitName={selectedUnits[0]?.label ?? ''}
          />
        )}
      </List>
      <CustomerQuestionForm add={(name) => addCustomerQuestion({ name })} />

      <Grid
        container
        justify={showAdditionalUnitsDropdown ? 'space-between' : 'flex-end'}
        alignItems="center"
        spacing={2}
      >
        {showAdditionalUnitsDropdown && (
          <Grid item xs={12} sm={6} style={{ minWidth: 260 }}>
            <FormControl fullWidth>
              <SelectField
                name="additionalCompanyUnits"
                isMulti
                placeholder={formatMessage(
                  { id: 'law-portal.manage.update-laws.dialog.companyUnit.placeholder' },
                  { alias: getCompanyUnitName(formatMessage, false) },
                )}
                options={additionalUnitOptions}
                errorTexts={[]}
                initialState={additionalUnits}
                onChange={handleAdditionalUnitsChange}
              />
            </FormControl>
          </Grid>
        )}
        <Grid item>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleSaveClick}
            style={{ minWidth: 140, fontWeight: 700 }}
          >
            <FormattedMessage id="form.save" />
          </Button>
        </Grid>
      </Grid>

      <Dialog open={confirmDialogOpen} onClose={closeConfirmDialog} aria-labelledby="confirm-overwrite-dialog-title">
        <DialogTitle id="confirm-overwrite-dialog-title">
          <FormattedMessage id="law-portal.manage.checklist.overwriteDialog.title" defaultMessage="Confirm Checklist Update" />
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <FormattedMessage
              id="law-portal.manage.checklist.overwriteDialog.body"
              defaultMessage="This will overwrite the existing checklist for {unitNames}. This cannot be undone."
              values={{ unitNames: additionalUnitNames }}
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} color="default">
            <FormattedMessage id="form.cancel" defaultMessage="Cancel" />
          </Button>
          <Button onClick={handleConfirmSave} color="primary" variant="contained" autoFocus>
            <FormattedMessage id="law-portal.manage.checklist.overwriteDialog.confirm" defaultMessage="Save & Overwrite" />
          </Button>
        </DialogActions>
      </Dialog>
    </Panel>
  );
};